import React from 'react';
import { MdAttachMoney, MdPsychology, MdSavings } from 'react-icons/md';
import Modal from '../Common/Modal';
import { formatDate, formatMoney, humanize } from '../../Utils/stediUtils';
import { Detail, FixSteps, Kicker, MUTED, PANEL_STYLE, ResultBanner, TITLE } from './StediResultParts';

// A payer rejection is headed by the payer's own reason (e.g. "Subscriber/Insured Not Found").
function outcome(result, errors) {
  if (result.payerRejected) {
    const [first] = errors;
    return {
      tone: 'warning',
      label: first?.description || 'Check Not Completed',
      detail: first?.followupAction || result.rejectionReason || 'The payer could not complete this check.',
    };
  }
  const status = result.coverageStatus;
  if (!status) {
    return { tone: 'neutral', label: 'No Mental Health Status', detail: 'The payer answered, but returned no mental health coverage status.' };
  }
  if (/^active/i.test(status)) {
    return { tone: 'success', label: humanize(status), detail: 'Mental health benefits are active for this patient.' };
  }
  return { tone: 'danger', label: humanize(status), detail: 'Mental health coverage is not active for this patient.' };
}

const BenefitTile = ({ icon: Icon, label, value, hint }) => (
  <div className="col-12 col-md-4">
    <div className="h-100 rounded-4 border p-3 d-flex align-items-center gap-3" style={PANEL_STYLE}>
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-3 hc-bg-primary text-white flex-shrink-0"
        style={{ width: 44, height: 44 }}
      >
        <Icon size={22} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="small fw-semibold" style={MUTED}>{label}</div>
        <div className={value ? 'fw-bold fs-5 lh-sm' : 'fw-semibold fst-italic'} style={value ? TITLE : { color: 'var(--hc-text-faint)' }}>
          {value || 'Not returned'}
        </div>
        <div className="small" style={{ color: 'var(--hc-text-faint)' }}>{hint}</div>
      </div>
    </div>
  </div>
);

// Result of one real-time eligibility check, shown before opening the saved record.
// `result` is what POST /stedi/eligibility returns: the stored mental health summary plus Stedi's `response`.
export default function EligibilityResultModal({ result, onClose }) {
  if (!result) return null;

  const response = result.response ?? {};
  const errors = Array.isArray(response.errors) ? response.errors : [];
  const { tone, label, detail } = outcome(result, errors);

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      scrollable
      icon={<MdPsychology size={22} />}
      title="Eligibility Result"
      subtitle={[result.patientName, result.memberId && `Member ${result.memberId}`].filter(Boolean).join(' · ')}
      closeOnBackdrop={false}
    >
      <div className="d-flex flex-column gap-4">
        <ResultBanner
          tone={tone}
          label={label}
          detail={detail}
          payerName={response.payer?.name?.organization}
          payerId={response.payerId ?? response.payer?.identification}
        />

        {result.payerRejected ? (
          <FixSteps errors={errors} tone={tone} />
        ) : (
          <section>
            <Kicker>Mental health benefits</Kicker>
            <div className="row g-3">
              <BenefitTile
                icon={MdPsychology}
                label="Coverage"
                value={result.coverageStatus && humanize(result.coverageStatus)}
                hint="Service type MH"
              />
              <BenefitTile
                icon={MdAttachMoney}
                label="Copay"
                value={result.copay != null && formatMoney(result.copay)}
                hint="In-network, per visit"
              />
              <BenefitTile
                icon={MdSavings}
                label="Deductible Remaining"
                value={result.deductible != null && formatMoney(result.deductible)}
                hint="In-network, individual"
              />
            </div>
          </section>
        )}

        <section>
          <Kicker>Check details</Kicker>
          <div className="rounded-4 border p-3" style={PANEL_STYLE}>
            <div className="row g-3">
              <Detail label="Patient" value={result.patientName} />
              <Detail label="Date of Birth" value={result.dateOfBirth && formatDate(result.dateOfBirth)} />
              <Detail label="Member ID" value={result.memberId} />
              <Detail label="Provider NPI" value={response.provider?.npi} />
              <Detail label="Stedi Check ID" value={response.id} />
            </div>
          </div>
        </section>
      </div>
    </Modal>
  );
}
